/**
 * Brief 3.6.5 — SLA timer pill.
 *
 * Single source for SLA visual semantics across `/admin/merchants` (column)
 * and `/admin/merchants/{id}` (sticky header). Reads the `sla` snapshot
 * computed by `services/sla.compute_sla` on the backend.
 *
 * Tones:
 *   • ok      — green   (>24h remaining, OR met before deadline)
 *   • amber   — amber   (≤24h remaining and not yet red)
 *   • red     — rose    (≤6h remaining OR breached AND still in submitted)
 *   • neutral — zinc    (not_applicable: never submitted, draft, etc.)
 */
import { AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

const TONE_CLS = {
  ok:      "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100",
  amber:   "border-amber-400/30 bg-amber-400/[0.10] text-amber-100",
  red:     "border-rose-400/40 bg-rose-400/[0.10] text-rose-100",
  neutral: "border-white/[0.08] bg-white/[0.02] text-zinc-400",
};

function formatHours(h) {
  if (h === null || h === undefined) return "—";
  const abs = Math.abs(h);
  if (abs >= 24) {
    const days = Math.floor(abs / 24);
    const rem = Math.round(abs - days * 24);
    return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
  }
  if (abs >= 1) return `${abs.toFixed(1)}h`;
  return `${Math.round(abs * 60)}m`;
}

function pillLabel(sla) {
  switch (sla?.state) {
    case "in_progress": {
      const h = sla.hours_remaining;
      return h <= 0 ? "Overdue" : `${formatHours(h)} left`;
    }
    case "breached":   return `${formatHours(sla.hours_overdue)} overdue`;
    case "met":        return "On time";
    case "missed":     return "Missed SLA";
    case "not_applicable":
    default:           return "—";
  }
}

function pillIcon(sla) {
  const state = sla?.state;
  if (state === "breached") return AlertTriangle;
  if (state === "in_progress" && sla?.tone === "red") return AlertTriangle;
  if (state === "met") return CheckCircle2;
  return Clock;
}

export default function SLAPill({ sla, size = "md", testId = "sla-pill" }) {
  if (!sla || sla.state === "not_applicable") {
    return null;
  }
  const tone = sla.tone || "neutral";
  const Icon = pillIcon(sla);
  const sizing = size === "sm"
    ? "text-[10.5px] px-2 py-0.5 gap-1"
    : "text-[11.5px] px-2.5 py-1 gap-1.5";
  return (
    <span
      data-testid={`${testId}-${tone}`}
      title={sla.deadline ? `48h SLA · deadline ${new Date(sla.deadline).toLocaleString()}` : undefined}
      className={`inline-flex items-center rounded-full border uppercase tracking-[0.14em] font-medium ${TONE_CLS[tone]} ${sizing}`}
    >
      <Icon className="h-3 w-3" />
      <span>SLA · {pillLabel(sla)}</span>
    </span>
  );
}
